/**
 * 獲取當前用戶角色的 Hook
 * 使用 SWR 共用 /api/members/me 快取，減少多組件重複請求
 */

import { useState, useEffect, useMemo } from 'react';
import useSWR from 'swr';
import type { UserRole, MemberRole } from '@pos/shared/permissions';
import { canAccessAdmin } from '@pos/shared/permissions';

// 重新導出類型，保持向後相容
export type { UserRole, MemberRole };

const API_BASE =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL
    ? import.meta.env.VITE_API_BASE_URL
    : 'http://localhost:8787';

interface MembersMeResponse {
  success: boolean;
  data?: { role?: string };
}

/**
 * 獲取當前用戶的角色（SWR 共用快取，同 tenant 內多組件只打一次）
 */
export function useCurrentUserRole(): {
  role: UserRole;
  isLoading: boolean;
  error: Error | null;
} {
  const [tenantId, setTenantId] = useState<string | null>(() =>
    typeof window !== 'undefined' ? localStorage.getItem('tenant_id') : null
  );
  const [token, setToken] = useState<string | null>(() =>
    typeof window !== 'undefined' ? localStorage.getItem('tenant_token') : null
  );

  useEffect(() => {
    const sync = () => {
      setToken(localStorage.getItem('tenant_token'));
      setTenantId(localStorage.getItem('tenant_id'));
    };
    sync();
    window.addEventListener('localStorageChange', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('localStorageChange', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const isLobbyPage =
    typeof window !== 'undefined' &&
    (window.location.pathname === '/tenant-select' ||
      window.location.pathname.includes('/tenant-select'));

  const swrKey =
    token && tenantId && !isLobbyPage
      ? (['/api/members/me', tenantId, token] as const)
      : null;

  const { data, error, isLoading } = useSWR<MembersMeResponse>(
    swrKey,
    async ([path, tenantIdKey, tokenKey]: readonly [
      string,
      string,
      string,
    ]) => {
      const res = await fetch(`${API_BASE}${path}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${tokenKey}`,
          'X-Tenant-ID': tenantIdKey,
        },
      });
      if (res.status === 404) {
        const body = await res.json().catch(() => ({}));
        const msg = (body.message || '').toLowerCase();
        const code = (body.code || '').toUpperCase();
        const isTenantNotFound =
          code === 'TENANT_NOT_FOUND' ||
          (msg.includes('tenant') && msg.includes('not found')) ||
          msg.includes('商家不存在') ||
          msg.includes('找不到用戶資訊');
        if (isTenantNotFound) {
          const currentTenantId = localStorage.getItem('tenant_id');
          localStorage.removeItem('tenant_id');
          if (currentTenantId) {
            localStorage.removeItem(`tenant_name_${currentTenantId}`);
          }
          window.dispatchEvent(new Event('localStorageChange'));
          window.location.replace('/tenant-select');
        }
      }
      if (!res.ok) {
        const err = new Error(`取得角色失敗: ${res.status}`);
        (err as Error & { status: number }).status = res.status;
        throw err;
      }
      return res.json();
    },
    {
      dedupingInterval: 30000, // 30 秒內同 key 只打一次，多組件共用
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      keepPreviousData: true,
    }
  );

  const role: UserRole = useMemo(() => {
    if (!data?.success || !data?.data?.role) return null;
    return data.data.role as UserRole;
  }, [data]);

  return {
    role,
    isLoading: swrKey !== null && isLoading,
    error: error || null,
  };
}

/**
 * 檢查用戶是否有後台訪問權限
 * 使用共享的權限檢查函數，確保前後端邏輯一致
 */
export function useHasAdminAccess(): {
  hasAccess: boolean;
  isLoading: boolean;
  role: UserRole;
} {
  const { role, isLoading } = useCurrentUserRole();

  const hasAccess = useMemo(() => {
    return canAccessAdmin(role);
  }, [role]);

  return { hasAccess, isLoading, role };
}
